import * as THREE from 'three';

/**
 * Naive Surface Nets — voxel doluluk alanından PÜRÜZSÜZ yüzey üretir.
 * Marching-cubes'un 256-satırlık üçgen tablosu YOK: kenar tablosu cube_edges'ten
 * programatik üretilir (typo riski yok, sağlam). S.F. Gibson / Mikola Lysenko.
 *
 * Çıktı: pürüzsüz manifold yüzey (positions+indices) + her vertex için en yakın
 * voxel lineer indeksi (renklendirme/animasyon için).
 */

// Kenar tabloları (init'te bir kez hesaplanır)
const cubeEdges = new Int32Array(24);
const edgeTable = new Int32Array(256);
(function initTablolar() {
  let k = 0;
  for (let i = 0; i < 8; ++i) {
    for (let j = 1; j <= 4; j <<= 1) {
      const p = i ^ j;
      if (i <= p) { cubeEdges[k++] = i; cubeEdges[k++] = p; }
    }
  }
  for (let i = 0; i < 256; ++i) {
    let em = 0;
    for (let j = 0; j < 24; j += 2) {
      const a = !!(i & (1 << cubeEdges[j]));
      const b = !!(i & (1 << cubeEdges[j + 1]));
      em |= a !== b ? (1 << (j >> 1)) : 0;
    }
    edgeTable[i] = em;
  }
})();

export interface ISurfaceNetSonuc {
  positions: Float32Array;
  indices: Uint32Array;
  /** Her vertex → en yakın voxel lineer indeksi (renk örnekleme için) */
  hucreLin: Int32Array;
}

/**
 * @param field Signed alan: iç (dolu) < 0, dış > 0. (örn. iso − dolulukYumusak)
 * @param dims  [nx, ny, nz] örnek sayısı (voxel grid boyutu)
 * @param origin dünya min köşesi
 * @param hucreMm hücre kenarı (mm)
 */
export function surfaceNets(
  field: Float32Array, dims: [number, number, number],
  origin: THREE.Vector3, hucreMm: number,
): ISurfaceNetSonuc {
  const [nx, ny, nz] = dims;
  const R = [1, nx + 1, (nx + 1) * (ny + 1)];
  const grid = new Float32Array(8);
  const buffer = new Int32Array(R[2] * 2).fill(-1); // -1 = vertex yok (0 geçerli indeks!)
  let bufNo = 1;

  const vx: number[] = [];   // vertex dünya koordinatları (flat 3)
  const vlin: number[] = []; // vertex → voxel lin
  const idx: number[] = [];  // üçgen indisleri

  const x = [0, 0, 0];
  let n = 0;

  for (x[2] = 0; x[2] < nz - 1; ++x[2], n += nx, bufNo ^= 1, R[2] = -R[2]) {
    let m = 1 + (nx + 1) * (1 + bufNo * (ny + 1));
    for (x[1] = 0; x[1] < ny - 1; ++x[1], ++n, m += 2) {
      for (x[0] = 0; x[0] < nx - 1; ++x[0], ++n, ++m) {
        buffer[m] = -1; // bu hücre vertex üretmezse ping-pong'da bayat indeks kalmasın
        // 8 köşe değeri + iç/dış maskesi
        let mask = 0, g = 0, p = n;
        for (let k = 0; k < 2; ++k, p += nx * ny - 2 * nx) {
          for (let j = 0; j < 2; ++j, p += nx - 2) {
            for (let i = 0; i < 2; ++i, ++g, ++p) {
              const s = field[p];
              grid[g] = s;
              mask |= (s < 0) ? (1 << g) : 0;
            }
          }
        }
        if (mask === 0 || mask === 0xff) continue;

        const em = edgeTable[mask];
        const v = [0, 0, 0];
        let eSay = 0;
        for (let i = 0; i < 12; ++i) {
          if (!(em & (1 << i))) continue;
          ++eSay;
          const e0 = cubeEdges[i << 1], e1 = cubeEdges[(i << 1) + 1];
          const g0 = grid[e0], g1 = grid[e1];
          let t = g0 - g1;
          if (Math.abs(t) > 1e-9) t = g0 / t; else t = 0.5;
          for (let j = 0, kk = 1; j < 3; ++j, kk <<= 1) {
            const a = e0 & kk, b = e1 & kk;
            if (a !== b) v[j] += a ? 1.0 - t : t;
            else v[j] += a ? 1.0 : 0.0;
          }
        }
        const sc = 1.0 / eSay;
        const cx = x[0] + sc * v[0], cy = x[1] + sc * v[1], cz = x[2] + sc * v[2];

        buffer[m] = vx.length / 3;
        vx.push(origin.x + cx * hucreMm, origin.y + cy * hucreMm, origin.z + cz * hucreMm);
        // En yakın voxel lin (renk örnekleme)
        const lx = Math.min(nx - 1, Math.max(0, Math.round(cx)));
        const ly = Math.min(ny - 1, Math.max(0, Math.round(cy)));
        const lz = Math.min(nz - 1, Math.max(0, Math.round(cz)));
        vlin.push(lx + nx * (ly + ny * lz));

        // Yüzler (3 eksen): kenar işaret değiştiriyorsa quad → 2 üçgen
        for (let i = 0; i < 3; ++i) {
          if (!(em & (1 << i))) continue;
          const iu = (i + 1) % 3, iv = (i + 2) % 3;
          if (x[iu] === 0 || x[iv] === 0) continue;
          const du = R[iu], dv = R[iv];
          const a = buffer[m], b = buffer[m - du], c = buffer[m - du - dv], d = buffer[m - dv];
          // Komşu hücreler vertex üretmediyse (−1) yüzü atla → sliver/bozuk üçgen yok
          if (a < 0 || b < 0 || c < 0 || d < 0) continue;
          if (mask & 1) { idx.push(a, b, c, a, c, d); }
          else { idx.push(a, d, c, a, c, b); }
        }
      }
    }
  }

  return {
    positions: new Float32Array(vx),
    indices: new Uint32Array(idx),
    hucreLin: new Int32Array(vlin),
  };
}

/**
 * Voxel doluluğu (0/1) → signed alan (iç<0, dış>0). İNCE DUVAR KORUNUR:
 * dolu hücre kesin iç (−1), boş hücre dış (+1) — sadece DOLU hücreler hafifçe
 * komşu oranıyla yumuşatılır (eritmeden). Surface nets kenar-ortalamasıyla
 * zaten pürüzsüzleştirir; agresif bulanıklık ince cidarı yok ediyordu.
 */
export function dolulukSignedAlan(
  doluluk: Uint8Array, dims: [number, number, number], dilAdim = 1,
): Float32Array {
  const [nx, ny, nz] = dims;
  const nxy = nx * ny;
  const toplam = nx * ny * nz;
  // GENİŞLETME (dilation): ince cidarı kalınlaştırır → sürekli + "dolu" yüzey.
  // dilAdim kez 6-komşu genişletme uygulanır (kullanıcı kontrolü).
  let katmanA = new Uint8Array(toplam);
  for (let i = 0; i < toplam; i++) katmanA[i] = doluluk[i] === 1 ? 1 : 0;
  let katmanB = new Uint8Array(toplam);
  for (let adim = 0; adim < Math.max(0, dilAdim); adim++) {
    for (let z = 0; z < nz; z++) {
      for (let y = 0; y < ny; y++) {
        for (let x = 0; x < nx; x++) {
          const i = x + nx * (y + ny * z);
          let d = katmanA[i] === 1;
          if (!d) {
            // 3×3×3 (26-komşu) genişletme → çapraz boşlukları da kapatır
            for (let dz = -1; dz <= 1 && !d; dz++) {
              const zz = z + dz; if (zz < 0 || zz >= nz) continue;
              for (let dy = -1; dy <= 1 && !d; dy++) {
                const yy = y + dy; if (yy < 0 || yy >= ny) continue;
                for (let dx = -1; dx <= 1; dx++) {
                  const xx = x + dx; if (xx < 0 || xx >= nx) continue;
                  if (katmanA[xx + nx * (yy + ny * zz)]) { d = true; break; }
                }
              }
            }
          }
          katmanB[i] = d ? 1 : 0;
        }
      }
    }
    const t = katmanA; katmanA = katmanB; katmanB = t;
  }
  // EROZYON: kapama (closing) — dilAdim−1 kez geri küçült. Boşluklar DOLU kalır,
  // dış sınır ~orijinale döner (net ~1 voxel kalınlık → ince levha yüzleri çakışıp
  // KAYBOLMAZ, surface nets iki yüzü ayrı çıkarır) → ölçü ~doğru + deliksiz.
  const erozyonAdim = Math.max(0, dilAdim - 1);
  for (let adim = 0; adim < erozyonAdim; adim++) {
    for (let z = 0; z < nz; z++) {
      for (let y = 0; y < ny; y++) {
        for (let x = 0; x < nx; x++) {
          const i = x + nx * (y + ny * z);
          let d = katmanA[i] === 1;
          if (d) {
            // herhangi bir komşu boş/grid-dışı ise → erode (boş yap)
            for (let dz = -1; dz <= 1 && d; dz++) {
              const zz = z + dz;
              for (let dy = -1; dy <= 1 && d; dy++) {
                const yy = y + dy;
                for (let dx = -1; dx <= 1; dx++) {
                  const xx = x + dx;
                  if (xx < 0 || xx >= nx || yy < 0 || yy >= ny || zz < 0 || zz >= nz || !katmanA[xx + nx * (yy + ny * zz)]) { d = false; break; }
                }
              }
            }
          }
          katmanB[i] = d ? 1 : 0;
        }
      }
    }
    const t = katmanA; katmanA = katmanB; katmanB = t;
  }
  const out = new Float32Array(toplam);
  for (let i = 0; i < toplam; i++) out[i] = katmanA[i] === 1 ? -1 : 1; // iç (−1) / dış (+1)
  // DIŞ KABUK BOŞ: katı grid sınırına dayanırsa surface nets yüzeyi kapatmaz →
  // dış 1-voxel kabuğu zorla boş yap → katı her yönden sarılır → kapalı yüzey.
  for (let z = 0; z < nz; z++)
    for (let y = 0; y < ny; y++)
      for (let x = 0; x < nx; x++)
        if (x === 0 || y === 0 || z === 0 || x === nx - 1 || y === ny - 1 || z === nz - 1)
          out[x + nx * (y + ny * z)] = 1;
  return out;
}
